/*
 * Writing the freight cost xlsx file:
 * it turns the freight costs of a factory into the rows of its sheet.
 */

import { FreightCostTypes } from '../../types/freightCost/freightCostType';
import { ExportedSheetRow, FreightCostsByCostType } from '../../types/freightCost/freightCostXlsx';
import constants from '../constants';
import { EMPTY_CELL } from '../xlsx';
import {
  DISTANCE_COLUMN_HEADER,
  getFreightCostTypeHeader,
  OVER_RANGES_LABEL
} from './freightCostFormat';

const { OVER_RANGES_END_DISTANCE } = constants.FREIGHT_COST;

/**
 * Puts the bands of every type into a map, so that a cost can be looked up by its distance.
 *
 * @param {FreightCostsByCostType[]} types the freight costs of one factory by types
 * @returns {Map<string, Map<number, number>>} the freight cost of every type by distance range
 */
function collectCostsByType(types: FreightCostsByCostType[]): Map<string, Map<number, number>> {
  const costsByType = new Map<string, Map<number, number>>();

  for (const { type, costs } of types) {
    costsByType.set(
      type,
      new Map<number, number>(
        costs.map(({ endDistance, freightCost }) => [endDistance, freightCost])
      )
    );
  }

  return costsByType;
}

/**
 * The freight cost of a type in a distance range.
 *
 * @param {Map<string, Map<number, number>>} costsByType the freight costs
 * by type and distance range
 * @param {string} type the config key of the type
 * @param {number} distance the endDistance of the distance range
 * @returns {number | string} the freight cost, or an empty string when the type has none
 */
function getCostValue(
  costsByType: Map<string, Map<number, number>>,
  type: string,
  distance: number
): number | string {
  return costsByType.get(type)?.get(distance) ?? EMPTY_CELL;
}

/**
 * Builds the rows of one factory's sheet from its own freight costs.
 *
 * The sheet of a factory that uses the default freight costs remains empty.
 * If a type has no cost for a factory, its whole column will be left out.
 *
 * @param {FreightCostTypes} freightCostTypes the freight cost types of the mode,
 * in the order of the columns
 * @param {FreightCostsByCostType[]} types the own freight costs of the factory by types
 * @returns {ExportedSheetRow[]} the rows of the factory's sheet, the header row first
 */
export function buildFactorySheetRows(
  freightCostTypes: FreightCostTypes,
  types: FreightCostsByCostType[]
): ExportedSheetRow[] {
  const costsByType = collectCostsByType(types);

  const sheetTypes = Object.keys(freightCostTypes).filter((type) => costsByType.has(type));
  if (sheetTypes.length === 0) {
    return [[DISTANCE_COLUMN_HEADER]];
  }

  // the distance column is the union of the distance ranges of the types
  const distances = [
    ...new Set(sheetTypes.flatMap((type) => [...(costsByType.get(type)?.keys() ?? [])]))
  ]
    .filter((distance) => distance !== OVER_RANGES_END_DISTANCE)
    .sort((first, second) => first - second);

  const rows: ExportedSheetRow[] = [
    [
      DISTANCE_COLUMN_HEADER,
      ...sheetTypes.map((type) => getFreightCostTypeHeader(freightCostTypes, type))
    ]
  ];
  for (const distance of distances) {
    rows.push([distance, ...sheetTypes.map((type) => getCostValue(costsByType, type, distance))]);
  }

  // the "over the ranges" row is written only when at least one type has such a cost
  const hasOverRangesCost = sheetTypes.some((type) =>
    costsByType.get(type)?.has(OVER_RANGES_END_DISTANCE)
  );
  if (hasOverRangesCost) {
    rows.push([
      OVER_RANGES_LABEL,
      ...sheetTypes.map((type) => getCostValue(costsByType, type, OVER_RANGES_END_DISTANCE))
    ]);
  }

  return rows;
}
